import {createApi} from '@reduxjs/toolkit/dist/query/react'
import {baseQueryWithReauth} from './BaseQueryReauth'
import {IAppActions, IAppsModel, IAppStatus} from '../types/apps/AppsModel'
import {IAppCreateData} from '../template/partials/modals/create-app/IAppModels'
import {IAmoCRMUsersSyncResult} from '../types/apps/crm/amocrm/AmoCRMUserModel'
import {IBitrix24AppCheck, IBitrix24AppCheckRequest} from '../types/apps/crm/biitrix24/Bitrix24AppCheckModel'

interface IQueryParams {
  skip?: number
  limit?: number
  search?: string
}

interface ListResponse<T> {
  data: T[]
  total: number
}

interface IResponse {
  status: string
  detail: string
}

interface ILicenseUpdate {
  id: string
  data?: {
    is_commercial?: boolean
    maxusers?: number
    license_end?: string
    is_active?: boolean
  }
}
interface ICdrState {
  id: string
  data: {
    is_active: boolean
  }
}

export interface ICdrStateResponse {
  detail: string
}

export const appsAPI = createApi({
  reducerPath: 'appsAPI',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['apps', 'license'],
  endpoints: (build) => ({
    fetchAppsMe: build.query<IAppsModel[], {account_id?: string} | undefined>({
      query: (params) => ({
        url: '/apps/me',
        params: params?.account_id ? {account_id: params.account_id} : {},
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({id}) => ({type: 'apps' as const, id})), {type: 'apps', id: 'LIST'}]
          : [{type: 'apps', id: 'LIST'}],
    }),
    createApp: build.mutation<IResponse, IAppCreateData>({
      query: ({crm_domain, crm, pbx, region, is_auto_mode, time_zone, settings}) => ({
        url: '/apps/',
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: {
          crm_domain: crm_domain,
          crm: crm,
          pbx: pbx,
          region: region,
          is_auto_mode: is_auto_mode,
          time_zone: time_zone,
          settings: settings,
        },
      }),
      invalidatesTags: ['apps'],
    }),
    fetchAppById: build.query<IAppsModel, string>({
      query: (id) => ({
        url: `/apps/${id}`,
      }),
      providesTags: (result, error, id) => [{type: 'apps' as const, id}],
    }),
    fetchAppsUserById: build.query<IAppsModel[], {user_id: string; skip?: number; limit?: number}>({
      query: ({user_id, skip = 0, limit = 50}) => ({
        url: `/apps/users/${user_id}`,
        params: {skip: skip, limit: limit},
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({id}) => ({type: 'apps' as const, id})), {type: 'apps', id: 'LIST'}]
          : [{type: 'apps', id: 'LIST'}],
    }),
    fetchApps: build.query<ListResponse<IAppsModel>, IQueryParams>({
      query: ({search, skip = 0, limit = 50}) => ({
        url: '/apps/',
        params: {skip: skip, limit: limit, search: search!},
      }),
      providesTags: (result) =>
        result
          ? [...result.data.map(({id}) => ({type: 'apps' as const, id})), {type: 'apps', id: 'LIST'}]
          : [{type: 'apps', id: 'LIST'}],
    }),
    updateApp: build.mutation<IResponse, IAppsModel>({
      query: (app_settings) => ({
        url: '/apps/' + app_settings.id,
        method: 'PATCH',
        body: app_settings,
      }),
      invalidatesTags: ['apps'],
    }),
    syncAmoCRMUsers: build.mutation<IAmoCRMUsersSyncResult, string>({
      query: (appId) => ({
        url: `/crm/amocrm/apps/${appId}/users/sync`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, appId) => [{type: 'apps', id: appId}],
    }),
    checkBitrix24App: build.query<IBitrix24AppCheck, IBitrix24AppCheckRequest>({
      query: ({appId, modeIsCloud, credentials}) => ({
        url: `/crm/bitrix24/apps/${appId}/check`,
        method: 'POST',
        body: {mode_is_cloud: modeIsCloud, credentials},
      }),
    }),
    fetchAppStatus: build.query<IAppStatus, string>({
      query: (id) => ({
        url: `/apps/status/${id}`,
      }),
    }),
    actionApp: build.mutation<void, IAppActions>({
      query: ({id, action}) => ({
        url: `/apps/actions/${id}`,
        method: 'POST',
        body: {
          action: action,
        },
      }),
    }),
    updateLicense: build.mutation<void, ILicenseUpdate>({
      query: ({id, data}) => ({
        url: `/licenses/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['apps'],
    }),
    updateCdrState: build.mutation<ICdrStateResponse | void, ICdrState>({
      query: ({id, data}) => ({
        url: `/extensions/cdr/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['apps'],
    }),
    appDelete: build.mutation<void, string>({
      query: (app_id) => ({
        url: `/apps/${app_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['apps'],
    }),
  }),
})
